import { GetServerSidePropsContext, NextPage } from 'next'
import HomeTemplate from 'src/templates/HomeTemplate'
import useHeader from 'src/relay/Header/useHeader'
import { initWithLangAndLabels } from 'src/i18n'
import useFooter from 'src/relay/Footer/useFooter'
import Blocks from 'src/relay/Blocks'
import homepage from 'src/rest/requests/pages/homepage'
import RestApiClient from 'src/rest/client'
import useHero from 'src/relay/Hero/useHero'
import useLangSwitch from 'src/relay/LangSwitch/useLangSwitch'

import {
  GetServerSidePropsReturnType,
  PageProps,
} from '../relay/Common/PageProps'
import getConfiguration from '../rest/services/getConfiguration'
import { Page as PageData } from '../rest/types/Page'
import { SeoTransformer } from '../relay/Transformers/SeoTransformer'
import { sanitizeSSRProps } from '../utils/sanitizeSSRProps'

type Props = PageProps & {
  data: PageData
}

const HomePage: NextPage<Props> = ({
  data,
  configuration,
  locale: currentLocale,
}) => {
  const langSwitch = useLangSwitch({
    configuration,
    currentLocale,
    localized: data.localizations ?? [],
  })

  const header = useHeader({
    configuration,
    currentLocale,
    langSwitch,
  })
  const footer = useFooter({
    configuration,
    currentLocale,
    langSwitch,
    theme: data.theme,
  })
  const hero = useHero({ hero: data?.hero, theme: data.theme, large: true })
  return (
    <HomeTemplate headerProps={header} footerProps={footer} hero={hero}>
      <Blocks
        blocks={data.content}
        configuration={configuration}
        pageInfos={data}
        locale={currentLocale}
      />
    </HomeTemplate>
  )
}

export default HomePage

export async function getServerSideProps(
  context: GetServerSidePropsContext
): GetServerSidePropsReturnType<Props> {
  const locale = context?.locale ?? 'fr'

  const [configuration, homePageRs] = await Promise.all([
    getConfiguration(locale),
    RestApiClient.call(homepage({ locale })),
  ])

  const data = homePageRs.data || null

  const i18n = initWithLangAndLabels(locale, configuration.translations ?? {})

  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  )

  if (!data) {
    return {
      notFound: true,
      props: {
        i18n,
        configuration,
        locale,
      },
    }
  }

  return {
    props: {
      metas: SeoTransformer(context.resolvedUrl, data?.seo, locale),
      configuration,
      i18n,
      locale,
      // strips undefined values rejected by Next.js 15 serializer
      data: sanitizeSSRProps(data),
    },
  }
}
